import { Component, OnInit, ViewChild } from '@angular/core';
import { ControlsOf, FormGroupType } from '@app/core/utils/formgroup-type';
import {
  AttachMeterDto,
  MeterFrameDto,
  MeterSearchAvailableDto,
  MeterSearchAvailableQueryDto,
  MeterService,
  ReplaceMeterDto
} from '@app/api/nts';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { InputDateDirective } from '@shared/components/input/date/input-date.directive';
import { TranslatePipe } from '@ngx-translate/core';
import { InputDirective } from '@shared/components/input/input/input.directive';
import { FormApiService } from '@app/core/services/form-api.service';
import { TableConfig } from '@shared/interfaces/table/table-config';
import { Observable, switchMap } from 'rxjs';
import { removeNullKeys } from '@app/core/utils/remove-null-keys';
import { meterSimpleTableConfig } from '@shared/table-configs/meter/meter-simple.table';
import { TableComponent } from '@shared/components/table/table.component';
import { ButtonDirective } from '@shared/components/input/button/button.directive';
import { AlertService } from '@app/core/services/alert.service';
import { SortParam } from '@app/core/constants/sort-param';
import { MeterDto, MeteringPointService } from '@app/api/dmc';
import { HasUnsavedChanges } from '@app/core/guards/unsaved-changes.guard';

@Component({
  selector: 'app-meter-replace-attach-modal',
  imports: [
    ReactiveFormsModule,
    InputGroupComponent,
    LabelDirective,
    InputDateDirective,
    TranslatePipe,
    InputDirective,
    TableComponent,
    ButtonDirective,
    FormsModule
  ],
  templateUrl: './meter-replace-attach-modal.component.html',
  styleUrl: './meter-replace-attach-modal.component.css'
})
export class MeterReplaceAttachModalComponent implements OnInit, HasUnsavedChanges {
  @ViewChild('table', { static: false }) table!: TableComponent<MeterSearchAvailableDto>;

  public hasUnsavedChanges(): boolean {
    return this.form.dirty;
  }

  public meterFrame?: MeterFrameDto;
  public meter?: MeterDto;

  /** Submit Form **/
  public form: FormGroupType<AttachMeterDto | ReplaceMeterDto> = new FormGroup<ControlsOf<AttachMeterDto | ReplaceMeterDto>>({
    meterId: new FormControl(null, Validators.required),
    meterFrameId: new FormControl(null, Validators.required),
    commissioningDate: new FormControl(null, Validators.required),

    // @ts-expect-error
    oldMeterId: new FormControl(),
    meterNumber: new FormControl(null, Validators.required),
    alternativeMeterNumber: new FormControl(null, Validators.required),
    serialNumber: new FormControl(null, Validators.required),
  })

  /** Search Form **/
  public searchForm: FormGroupType<MeterSearchAvailableQueryDto> = new FormGroup<ControlsOf<MeterSearchAvailableQueryDto>>({
    commissioningDate: new FormControl(),
    meterNumber: new FormControl(),
    alternativeMeterNumber: new FormControl(),
    serialNumber: new FormControl(),
  })

  /** Table **/
  public meterSimpleTableConfig: TableConfig<MeterSearchAvailableDto> = {
    ...meterSimpleTableConfig,
    id: 'meter-attach-replace-table',
    serviceCallback: (pageSize: number, page: number, sort?: SortParam) => this.handleSearch(pageSize, page, sort),
  }

  public selectedElements: MeterSearchAvailableDto[] = [];

  /** Config **/
  public oldMeterId?: string;
  public meterFrameId?: string;


  constructor(
    private readonly formApiService: FormApiService,
    private readonly meterService: MeterService,
    private readonly meteringPointService: MeteringPointService,
    private readonly alertService: AlertService,
  ) {
    this.alertService.info('modal', 'meter.replaceAttach.info');
  }

  public handleSearch(pageSize: number, page: number, sort?: SortParam): Observable<any> {
    this.searchForm.get('commissioningDate')?.setValue(this.form.get('commissioningDate')?.value, { emitEvent: false });
    const query = removeNullKeys(this.searchForm.value);
    return this.meterService.meterSearchAvailablePost({ ...query, pageSize, page, filterDiscarded: true } as MeterSearchAvailableQueryDto);
  }

  public ngOnInit(): void {
    if (this.meterFrameId) {
      this.form.get('meterFrameId')?.setValue(this.meterFrameId);
    } else {
      this.alertService.open('modal', 'danger', 'meter.replaceAttach.noMeterFrame');
    }

    this.form.get('commissioningDate')?.valueChanges.subscribe((value) => {
      this.searchForm.get('commissioningDate')?.setValue(value);
    })

    if (this.oldMeterId) {
      // @ts-expect-error
      this.form.get('oldMeterId')?.setValue(this.oldMeterId);
    }
  }

  public selectionChanged(element: any): void {
    if (element.length === 0) return;

    const data: any = {
      meterId: element[0].id,
      meterNumber: element[0].meterNumber,
      alternativeMeterNumber: element[0].alternativeMeterNumber,
      serialNumber: element[0].serialNumber,
    }

    this.form.patchValue(data);
  }

  public attachOrReplace(): void {
    const isReplace = !!this.oldMeterId;

    this.formApiService.submit(
      this.form,
      (data) =>
        isReplace
          ? this.meteringPointService.meteringPointValidateChangeMeterPost({
              newMeterId: data.meterId,
              oldMeterId: data.oldMeterId,
              newMeterCommissioningDate: data.commissioningDate,
            }).pipe(switchMap(() => this.meterService.meterReplacePost(data)))
          : this.meterService.meterAttachPost(data),
      {
        preprocess: (data: any) =>
          isReplace
            ? {
              oldMeterId: data.oldMeterId,
              meterId: data.meterId,
              meterFrameId: data.meterFrameId,
              commissioningDate: data.commissioningDate,
            }
            : {
              meterId: data.meterId,
              meterFrameId: data.meterFrameId,
              commissioningDate: data.commissioningDate,
            },
        closeModal: true,
        successMessage: isReplace
          ? 'meter.replaceAttach.replace.success'
          : 'meter.replaceAttach.attach.success',
      }
    );
  }

}
